/* import __COLOCATED_TEMPLATE__ from './ticket-type-select.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
// @ts-ignore
import intermoji from '@intercom/intermoji';
import type Conversation from 'embercom/objects/inbox/conversation';
import type Session from 'embercom/services/session';
import type { TicketType } from 'embercom/objects/inbox/ticket';
import type InboxState from 'embercom/services/inbox-state';

interface Args {
  conversation: Conversation;
  onSelectTicketType?: (ticketTypeId: number) => void;
}

interface Signature {
  Args: Args;
}

interface DropdownItem {
  value: number;
  text: string;
  emojiUrl?: string;
  component?: string;
  componentAttrs?: { emojiUrl?: string };
}

export default class TicketTypeSelect extends Component<Signature> {
  @service declare inboxHotkeys: any;
  @service declare inboxState: InboxState;
  @service declare session: Session;

  get currentTicketType(): TicketType | undefined {
    return this.args.conversation.ticketType;
  }

  get availableTicketTypes(): TicketType[] {
    if (!this.currentTicketType) {
      return [];
    }

    let allTicketTypes = this.session.workspace.ticketTypes.value || [];

    // Filter ticket types by same category and active status
    let filteredTypes = allTicketTypes.filter(
      (ticketType: TicketType) =>
        !ticketType.archived && ticketType.category === this.currentTicketType!.category,
    );

    // Always include the current ticket type in the list, even if it would be filtered out
    // This ensures IcSelect's selectedValue always appears in the items list
    let currentTypeIncluded = filteredTypes.some((tt) => tt.id === this.currentTicketType!.id);
    if (!currentTypeIncluded) {
      return [this.currentTicketType, ...filteredTypes];
    }

    return filteredTypes;
  }

  get dropdownItems(): DropdownItem[] {
    return this.availableTicketTypes.map((ticketType) => {
      let emojiUrl = ticketType.emoji ? intermoji.twemojiSVGUri(ticketType.emoji) : undefined;
      return {
        value: ticketType.id,
        text: ticketType.name,
        emojiUrl,
        component: 'inbox2/ticket-attribute/ticket-type-select-item',
        componentAttrs: { emojiUrl },
      };
    });
  }

  get selectedValue(): number | undefined {
    return this.currentTicketType?.id;
  }

  get emojiUrl(): string | undefined {
    return this.currentTicketType?.emoji
      ? intermoji.twemojiSVGUri(this.currentTicketType.emoji)
      : undefined;
  }

  get ticketTypeDescription(): string {
    return this.currentTicketType?.description ?? '';
  }

  get selectedTicketTypeName(): string {
    return this.currentTicketType?.name ?? '';
  }

  get resetKeyOrZero(): number {
    return this.inboxState.ticketTypeSelectResetKey;
  }

  @action
  onSelectItem(ticketTypeId: number) {
    // Only trigger callback if selecting a different ticket type
    if (ticketTypeId !== this.currentTicketType?.id && this.args.onSelectTicketType) {
      this.args.onSelectTicketType(ticketTypeId);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::TicketAttribute::TicketTypeSelect': typeof TicketTypeSelect;
    'inbox2/ticket-attribute/ticket-type-select': typeof TicketTypeSelect;
  }
}
